import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { settingsAPI, contactMessagesAPI, branchesAPI, contactSubjectsAPI } from '../api';

const Contact = () => {
  const [settings, setSettings] = useState(null);
  const [branches, setBranches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // Update title when component mounts or settings load
  useEffect(() => {
    document.title = `İletişim - ${settings?.site_name || 'Cinefora'}`;
  }, [settings]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [settingsRes, branchesRes, subjectsRes] = await Promise.all([
        settingsAPI.get(),
        branchesAPI.getAll(),
        contactSubjectsAPI.getAll({ is_active: true })
      ]);
      setSettings(settingsRes.data);
      setBranches(branchesRes.data);
      setSubjects(subjectsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await contactMessagesAPI.create(formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      
      // Hide success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError('Mesajınız gönderilemedi. Lütfen tekrar deneyin.');
      console.error('Error sending message:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (!settings) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-white">Yükleniyor...</p>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>İletişim - {settings.site_name || 'Cinefora'}</title>
        <meta name="description" content={`${settings.site_name || 'Cinefora'} ile iletişime geçin. Müşteri hizmetleri, şikayet ve önerileriniz için bizimle iletişime geçebilirsiniz.`} />
        <meta name="keywords" content="iletişim, müşteri hizmetleri, telefon, email, cinefora" />
      </Helmet>
      <Header />
      <div className="container mx-auto px-6 py-32">
        <h1 className="text-4xl font-bold text-white mb-8">İletişim</h1>
        
        {/* İletişim Bilgileri */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <Card className="bg-gray-900 border-gray-800 p-6 text-center">
            <Mail size={32} className="text-[#FDB714] mx-auto mb-4" />
            <h3 className="text-white font-bold mb-2">E-posta</h3>
            <a href={`mailto:${settings.contact_email || 'info@cinefora.com'}`} className="text-gray-300 hover:text-[#FDB714] transition-colors">
              {settings.contact_email || 'info@cinefora.com'}
            </a>
          </Card>
          
          <Card className="bg-gray-900 border-gray-800 p-6 text-center">
            <MapPin size={32} className="text-[#FDB714] mx-auto mb-4" />
            <h3 className="text-white font-bold mb-2">Salonlarımız</h3>
            <p className="text-gray-300">Türkiye genelinde {branches.length} noktada hizmetinizdeyiz</p>
          </Card>
        </div>

        <Card className="bg-gray-900 border-gray-800 p-8 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-6">Bize Ulaşın</h2>
          
          {success && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-400">
              Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.
            </div>
          )}
          
          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 block mb-2">Adınız Soyadınız *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-transparent focus:ring-2 focus:ring-[#FDB714] transition-all"
                  placeholder="Adınız Soyadınız"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">E-posta *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-transparent focus:ring-2 focus:ring-[#FDB714] transition-all"
                  placeholder="ornek@email.com"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 block mb-2">Telefon</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-transparent focus:ring-2 focus:ring-[#FDB714] transition-all"
                  placeholder="0xxx xxx xx xx"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Konu *</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-transparent focus:ring-2 focus:ring-[#FDB714] transition-all"
                >
                  <option value="">Konu Seçin</option>
                  {subjects.map((subject) => (
                    <option key={subject._id} value={subject.name}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-gray-300 block mb-2">Mesajınız *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-transparent focus:ring-2 focus:ring-[#FDB714] transition-all"
                placeholder="Mesajınızı detaylı bir şekilde yazın..."
              />
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <p className="text-gray-400 text-sm">
                <span className="text-[#FDB714]">*</span> işaretli alanlar zorunludur. Mesajınız en kısa sürede değerlendirilecek ve size dönüş yapılacaktır.
              </p>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FDB714] hover:bg-[#e5a510] text-black font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Gönderiliyor...' : 'Mesajı Gönder'}
            </button>
          </form>
        </Card>

        {/* Salonlarımız */}
        {branches.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-white mb-4">Salonlarımız</h2>
            <p className="text-gray-400 mb-8">Size en yakın salonumuzu bulun ve güncel seanslar için detay sayfasına göz atın</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {branches.map((branch) => (
                <Link 
                  key={branch._id}
                  to={`/salonlar/${branch.slug || branch._id}`}
                  className="block"
                >
                  <Card className="bg-gray-900 border-gray-800 p-6 hover:border-[#FDB714] transition-all cursor-pointer h-full group">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-bold text-white group-hover:text-[#FDB714] transition-colors">{branch.name}</h3>
                      <ArrowRight size={20} className="text-[#FDB714] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="space-y-3 text-gray-400 text-sm">
                      <div className="flex items-start gap-3">
                        <MapPin size={18} className="text-[#FDB714] flex-shrink-0 mt-1" />
                        <span>{branch.address}, {branch.city}</span>
                      </div>
                      {branch.phone && (
                        <div className="flex items-center gap-3">
                          <Phone size={18} className="text-[#FDB714] flex-shrink-0" />
                          <span className="hover:text-[#FDB714] transition-colors">
                            {branch.phone}
                          </span>
                        </div>
                      )}
                      {branch.email && (
                        <div className="flex items-center gap-3">
                          <Mail size={18} className="text-[#FDB714] flex-shrink-0" />
                          <span className="hover:text-[#FDB714] transition-colors break-all">
                            {branch.email}
                          </span>
                        </div>
                      )}
                      {branch.halls_count && (
                        <div className="pt-2 border-t border-gray-800">
                          <span className="text-gray-500">{branch.halls_count} Salon</span>
                        </div>
                      )}
                      {branch.facilities && branch.facilities.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {branch.facilities.map((facility, index) => (
                            <span key={index} className="text-xs bg-gray-800 text-[#FDB714] px-2 py-1 rounded">
                              {facility}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
